import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Rocket } from "lucide-react";

const ICON = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle };
const TONE = { pass: "text-good border-good", warn: "text-warn border-warn", fail: "text-bad border-bad" };
const VERDICT_TONE = {
  "READY FOR LIVE": "border-good bg-good/10",
  "READY PENDING LIVE CREDENTIALS": "border-warn bg-warn/10",
  BLOCKED: "border-bad bg-bad/10",
};

// Directorial Sign-off — the go/no-go artifact before hitting live Stripe/Plaid.
export const LaunchReadiness = () => {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { setReport((await api.get("/launch/readiness")).data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!report) return null;
  return (
    <div className="hard-card p-6 mt-8" data-testid="launch-readiness">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Rocket className="w-4 h-4" /> § HANDSHAKE SUCCESS REPORT</div>
          <div className="font-mono text-[10px] text-muted-foreground">
            Generated {new Date(report.generated_at).toLocaleString()} · Stripe <b>{report.stripe_mode}</b> · Plaid <b>{report.plaid_mode}</b>
          </div>
        </div>
        <button onClick={load} disabled={busy} data-testid="launch-readiness-refresh" className="border-2 border-ink px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
          <RefreshCw className="w-3 h-3" /> {busy ? "Checking…" : "Re-run"}
        </button>
      </div>

      <div className={`border-2 p-4 mb-4 ${VERDICT_TONE[report.verdict] || "border-ink"}`} data-testid="launch-verdict">
        <div className="font-heading font-black text-xl">{report.verdict}</div>
        <div className="font-mono text-[11px] mt-1">
          {report.blocking_count} blocking · {report.pending_count} pending
          {!report.live_ready && " — swap in live Stripe and production Plaid credentials via the Secrets tab, then redeploy."}
        </div>
      </div>

      <ul className="divide-y-2 divide-ink/15" data-testid="launch-checks">
        {report.checks.map((c) => {
          const Icon = ICON[c.status] || AlertTriangle;
          return (
            <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`launch-check-${c.id}`}>
              <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${TONE[c.status]?.split(" ")[0]}`} />
              <div>
                <div className="font-mono text-xs font-black">{c.label}</div>
                <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">{c.detail}</div>
              </div>
              <span className={`ml-auto shrink-0 border-2 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${TONE[c.status]}`}>
                {c.status}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 border-2 border-ink overflow-x-auto">
        <table className="w-full min-w-[420px] font-mono text-[11px]" data-testid="launch-fee-samples">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-2 uppercase tracking-widest">Sample net pay</th>
              <th className="text-right px-3 py-2 uppercase tracking-widest">Expected fee</th>
              <th className="text-right px-3 py-2 uppercase tracking-widest">Computed fee</th>
            </tr>
          </thead>
          <tbody>
            {report.fee_samples.map((f, i) => (
              <tr key={f.net_usd} className={i % 2 ? "bg-[#F9F9F9]" : "bg-white"}>
                <td className="px-3 py-2 border-t border-ink/20">${f.net_usd.toFixed(2)}</td>
                <td className="px-3 py-2 border-t border-ink/20 text-right">${f.expected_fee_usd.toFixed(2)}</td>
                <td className={`px-3 py-2 border-t border-ink/20 text-right font-black ${f.expected_fee_usd === f.computed_fee_usd ? "text-good" : "text-bad"}`}>
                  ${f.computed_fee_usd.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
